import { Field, Form, Formik } from "formik";
import calenderIcon from "../assets/images/calendar_icon_white.png";
import listIcon from "../assets/images/list_icon_white.png";
import classNames from "classnames";
import { toast } from "react-toastify";
import Select from "../components/Select";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { handleLogout } from "../config/axios";
import {
  fetchUserInfo,
  getAllAgentCodes,
  processAgentCodeValue,
} from "../services/userService";
import decryptData from "../decrypt";
import { apiFetch } from '../config/fetch';

type Agent = {
  value: string;
  label: string;
  db2_user_id: string;
};

export default function NewAppointment() {
  const [filters, setFilters]: any = useState([]);
  const [customer, setCustomer] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [agentFilters, setAgentFilters] = useState<Agent[]>([]);
  const [userInfo, setUserInfo] = useState(null);

  const [theme, setTheme] = useState("light");

  const currentTheme = useSelector((state: any) => state.menu.theme);

  useEffect(() => {
    setTheme(currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    if (agentFilters.length > 0) {
      fetchFilters();
    }
  }, [agentFilters]);

  useEffect(() => {
    const initializeData = async () => {
      // Fetch agents first to establish user role and agent list
      await fetchUserData();
    };
    initializeData();
  }, []);

  const fetchUserData = async (): Promise<string[] | undefined> => {
    try {
      // Use the centralized userService to fetch user info
      const { user, isSupervisor, agentCodes, agents } = await fetchUserInfo();

      if (!user) {
        console.error("No user data returned from fetchUserInfo");
        return [];
      }

      setUserInfo(user.id); // Save the user ID
      setUserRole(isSupervisor ? "supervisor" : "agent");

      // Raccogli tutti i codici agente pertinenti all'utente
      let allRelevantCodes: string[] = [];

      // Aggiungi i codici dell'utente corrente
      allRelevantCodes = [...allRelevantCodes, ...agentCodes];

      if (isSupervisor && agents.length > 0) {
        // Process supervisor's team agents
        const formattedAgents = agents.map((member) => {
          // IMPORTANTE: Non unire i codici con virgole per i valori
          // ma teniamo l'array originale e lo convertiamo in JSON
          const codeValue = JSON.stringify(member.all_codes);

          // Aggiungi tutti i codici di questo agente alla lista completa
          if (member.all_codes && member.all_codes.length > 0) {
            allRelevantCodes = [...allRelevantCodes, ...member.all_codes];
          }

          return {
            value: codeValue, // Store JSON stringified array: ["010","031"]
            label: `${member.first_name} ${member.last_name}`,
            db2_user_id: codeValue, // Mantiene lo stesso formato
            raw_codes: member.all_codes, // Conserva l'array originale
          };
        });

        setAgentFilters(formattedAgents);

        // Log di tutti i codici raccolti
        console.log(
          "Tutti i codici agente pertinenti (supervisor+agents):",
          allRelevantCodes
        );

        // Rimuovi duplicati e valori vuoti
        const uniqueCodes = [...new Set(allRelevantCodes)].filter(Boolean);
        console.log("Codici unici per le API:", uniqueCodes);

        return uniqueCodes;
      } else {
        // Per agenti normali: usa i propri codici
        // IMPORTANTE: Mantieni l'array originale
        const codeValue = JSON.stringify(agentCodes);
        const agentObj = {
          value: codeValue,
          label: `${user.firstName || ""} ${user.lastName || ""}`,
          db2_user_id: codeValue,
          raw_codes: agentCodes,
        };

        setAgentFilters([agentObj]);
        // Log di tutti i codici raccolti (solo quelli dell'agente)
        console.log("Codici agente singolo:", agentCodes);

        return agentCodes;
      }
    } catch (error) {
      console.error("Error fetching user data from keycloak:", error);
      return [];
    }
  };

  const fetchFilters = async () => {
    try {
      // Get all agent codes using the centralized function
      const processedAgentCodes = await getAllAgentCodes();

      console.log("Codici agente elaborati per /utenti:", processedAgentCodes);

      const requestBody = {
        agenti: processedAgentCodes,
        page: 1,
        pageSize: 1000,
      };

      const request = await apiFetch(`/nodeapi/utenti`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (request.status !== 200 && request.status !== 201) {
        handleLogout(request, "/nodeapi/utenti", JSON.stringify(requestBody));
        return;
      }

      const result = await request.json();

      // Decodifica i dati criptati
      if (result.encryptedData && result.encryptedKey && result.iv) {
        console.log("Decodifica dati criptati...");
        const decrypted = await decryptData(
          result.encryptedData,
          result.encryptedKey,
          result.iv
        );

        if (decrypted) {
          const decryptedJson = JSON.parse(decrypted);
          console.log("Dati decodificati:", decryptedJson);

          // Mappa l'array per estrarre solo i campi utili e imposta la proprietà searchFilters
          const mappedFilters = Array.isArray(decryptedJson.data)
            ? decryptedJson.data.map((item: any) => ({
                codice_cliente: item.CODICE_CLIENTE,
                ragione_sociale: item.RAGIONE_SOCIALE,
                numero_telefono: item.NUMERO_TELEFONO || "",
                email: item.EMAIL || "",
              }))
            : [];

          setFilters({ searchFilters: mappedFilters });
          return;
        }
      }

      // Fallback nel caso in cui i dati non siano criptati
      const mappedFilters = Array.isArray(result.data)
        ? result.data.map((item: any) => ({
            codice_cliente: item.CODICE_CLIENTE,
            ragione_sociale: item.RAGIONE_SOCIALE,
            numero_telefono: item.NUMERO_TELEFONO || "",
            email: item.EMAIL || "",
          }))
        : [];

      setFilters({ searchFilters: mappedFilters });
    } catch (e) {
      console.error("Errore durante il recupero dei filtri:", e);
    }
  };

  const handleFormSubmission = async (data, formikBag) => {
    console.log(userInfo);
    try {
      const { name, ...restData } = data;
      const request = await apiFetch(`/nodeapi/appointments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ...restData,
          customer: customer.codice_cliente,
          user_id: userInfo, // Includi l’id utente ottenuto da keycloak-protected
        }),
      });

      if (request.status !== 200 && request.status !== 201) {
        handleLogout(
          request,
          "/nodeapi/appointments",
          JSON.stringify({
            ...restData,
            customer: customer.codice_cliente,
            user_id: userInfo, // Includi l’id utente ottenuto da keycloak-protected
          })
        );
        return;
      }

      await formikBag.resetForm();
      toast.success("L'appuntamento è stato salvato con successo", {
        position: "top-right",
        className: "m-5",
      });
    } catch (e) {
      console.error(e);
      toast.error(e.message, {
        position: "top-right",
        className: "m-5",
      });
    }
  };

  const fetchCustomerInfo = async (val) => {
    try {
      if (val) {
        const value = JSON.parse(val);
        setCustomer(value); // Aggiorna lo stato customer
        const searchParams = new URLSearchParams();
        searchParams.append("codice_cliente", value.codice_cliente);
        searchParams.append("ragione_sociale", value.ragione_sociale);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSearchChange = async (value, setFieldValue) => {
    setFieldValue("name", value);
    await fetchCustomerInfo(value.value);
  };

  const newAppointmentSchema = Yup.object().shape({
    name: Yup.mixed().required("Il nome è obbligatorio"),
    title: Yup.string()
      .min(2, "Il titolo è troppo corto. Per favore descrivi di più")
      .required("Il titolo è obbligatorio"),
    message: Yup.string()
      .min(2, "Il messaggio è troppo breve. Per favore descrivi di più")
      .required("Il messaggio è obbligatorio"),
    contactMethod: Yup.mixed().oneOf(["sms", "mail", "nessuno"]),
  });

  return (
    <div className="px-5 py-8 sm:px-12 columns-3xl lg:columns-auto">
      <Formik
        validationSchema={newAppointmentSchema}
        initialValues={{
          name: "",
          date: "",
          time: "",
          title: "",
          message: "",
          contactMethod: "nessuno",
        }}
        onSubmit={handleFormSubmission}
      >
        {({ isSubmitting, values, errors, touched, setFieldValue }) => (
          <Form className="sm:max-w-4xl mx-auto">
            <div className=" sm:max-w-4xl px-5 sm:px-12 mx-auto flex flex-col justify-between mt-[50px] md:mt-50 mb-12">
              <h2 className="hidden sm:block text-4xl text-blue-500 sm:max-w-72 font-bold sm:mb-0">
                Nuovo appuntamento
              </h2>
            </div>

            <div className="mb-5">
              <Select
                classNames={{
                  container: (state) => `w-full`,
                  control: (state) =>
                    `py-2 border ${
                      errors.name ? "!border-red-600" : "border-neutral-300"
                    }`,
                }}
                onChange={(val) => handleSearchChange(val, setFieldValue)}
                value={values.name}
                placeholder="Nome Clienti"
                options={
                  filters &&
                  filters?.searchFilters?.map((sf, i) => ({
                    value: JSON.stringify(sf),
                    label: `${sf.ragione_sociale} (${sf.codice_cliente})`,
                  }))
                }
              />
              {errors.name && touched.name ? (
                <div className="text-red-600 my-2">
                  <p>{typeof errors?.name === "string" ? errors?.name : ""}</p>
                  <p>
                    {Array.isArray(errors?.name) ? errors?.name.join(", ") : ""}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex mb-5">
              <div className="w-full">
                <div className="date-input-container">
                  <Field
                    type="date"
                    name="date"
                    className={classNames({
                      "w-full placeholder-current::placeholder px-3 min-h-c block text-neutral-400 dark:text-neutral-400 font-semibold border bg-transparent text-lg focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none rounded":
                        true,
                      "border-red-600": errors.date,
                      "border-neutral-300": !errors.date,
                    })}
                    placeholder="Date"
                    style={{
                      colorScheme: theme === "dark" ? "dark" : "light",
                    }}
                  />
                  {errors.date && touched.date ? (
                    <div className="text-red-600 my-2">
                      <p>
                        {typeof errors?.date === "string" ? errors?.date : ""}
                      </p>
                      <p>
                        {Array.isArray(errors?.date)
                          ? errors?.date.join(", ")
                          : ""}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="w-full">
                <div className="date-input-time">
                  <Field
                    type="time"
                    name="time"
                    className={classNames({
                      "w-full placeholder-current::placeholder px-3 min-h-c block text-neutral-400 dark:text-neutral-400 font-semibold border bg-transparent text-lg focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none rounded":
                        true,
                      "border-red-600": errors.time,
                      "border-neutral-300": !errors.time,
                    })}
                    placeholder="Time"
                    style={{
                      colorScheme: theme === "dark" ? "dark" : "light",
                    }}
                  />
                  {errors.time && touched.time ? (
                    <div className="text-red-600 my-2">
                      <p>
                        {typeof errors?.time === "string" ? errors?.time : ""}
                      </p>
                      <p>
                        {Array.isArray(errors?.time)
                          ? errors?.time.join(", ")
                          : ""}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mb-5">
              <Field
                name="title"
                className={classNames({
                  "py-3 px-4 block w-full text-neutral-500 dark:text-neutral-400 font-semibold border bg-transparent rounded-none text-lg focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none":
                    true,
                  "border-red-600": errors.title,
                  "border-neutral-300": !errors.title,
                })}
                placeholder="Inserisci titolo"
              />
              {errors.title && touched.title ? (
                <div className="text-red-600 my-2">
                  <p>
                    {typeof errors?.title === "string" ? errors?.title : ""}
                  </p>
                  <p>
                    {Array.isArray(errors?.title)
                      ? errors?.title.join(", ")
                      : ""}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="mb-5">
              <Field
                as="textarea"
                rows={3}
                name="message"
                placeholder="Inserisci testo"
                className={classNames({
                  "py-3 px-4 block w-full text-neutral-500 dark:text-neutral-400 font-semibold border bg-transparent rounded-none text-lg focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none":
                    true,
                  "border-red-600": errors.message,
                  "border-neutral-300": !errors.message,
                })}
              ></Field>
              {errors.message && touched.message ? (
                <div className="text-red-600 my-2">
                  <p>
                    {typeof errors?.message === "string" ? errors?.message : ""}
                  </p>
                  <p>
                    {Array.isArray(errors?.message)
                      ? errors?.message.join(", ")
                      : ""}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="mb-6">
              <div className="py-3 flex items-center text-lg text-neutral-500 dark:text-neutral-400 after:flex-[1_1_0%] after:border-t after:border-neutral-300 dark:after:border-neutral-300 after:ms-6">
                METODO DI CONTATTO
              </div>

              <div className="flex">
                <label
                  htmlFor="contact_method_sms"
                  className={classNames({
                    "opacity-50": !customer?.numero_telefono,
                    "flex justify-center p-3 w-full border border-r-0 border-neutral-300 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white":
                      true,
                    "bg-blue-600 text-white": values.contactMethod === "sms",
                  })}
                >
                  <input
                    type="radio"
                    disabled={!customer?.numero_telefono}
                    name="contactMethod"
                    className="hidden shrink-0 mt-0.5 border-neutral-300 rounded-full text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-gray-800 dark:border-gray-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                    id="contact_method_sms"
                    onChange={(e: any) =>
                      setFieldValue("contactMethod", e?.target.value)
                    }
                    value="sms"
                    checked={values.contactMethod === "sms"}
                  />
                  <span className="text-lg ms-3">SMS</span>
                </label>

                <label
                  htmlFor="contact_method_mail"
                  className={classNames({
                    "opacity-50": !customer?.email,
                    "flex justify-center p-3 w-full border border-neutral-300 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white":
                      true,
                    "bg-blue-600 text-white": values.contactMethod === "mail",
                  })}
                >
                  <input
                    type="radio"
                    disabled={!customer?.email}
                    name="contactMethod"
                    className="hidden shrink-0 mt-0.5 border-neutral-300 rounded-full text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-gray-800 dark:border-gray-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                    id="contact_method_mail"
                    onChange={(e: any) =>
                      setFieldValue("contactMethod", e?.target.value)
                    }
                    value="mail"
                    checked={values.contactMethod === "mail"}
                  />
                  <span className="text-lg ms-3">Mail</span>
                </label>

                <label
                  htmlFor="contact_method_none"
                  className={classNames({
                    "flex justify-center p-3 w-full border border-neutral-300 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white":
                      true,
                    "bg-blue-600 text-white":
                      values.contactMethod === "nessuno",
                  })}
                >
                  <input
                    type="radio"
                    name="contactMethod"
                    className="hidden shrink-0 mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-gray-800 dark:border-gray-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                    id="contact_method_none"
                    onChange={(e: any) =>
                      setFieldValue("contactMethod", e?.target.value)
                    }
                    value="nessuno"
                    checked={values.contactMethod === "nessuno"}
                  />
                  <span className="text-lg ms-3">Nessuno</span>
                </label>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="py-3 px-5 w-full items-center gap-x-2 text-lg font-semibold border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
              >
                Invia richiesta appuntamento
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
